import React from 'react';
import { Package, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { OverviewBarChart } from '../reports/OverviewBarChart';
import { OverviewPieChart } from '../reports/OverviewPieChart';

export interface ProductWithDetails {
  id: string;
  name: string;
  revenue: number;
  profit: number;
  quantity: number;
  colorName?: string;
  colorHex?: string;
  sizeName?: string;
}

export interface ProductPerformanceProps {
  topProducts: ProductWithDetails[];
  bottomProducts: ProductWithDetails[];
  abcAnalysis: {
    A: { products: Array<{ id: string; name: string; value: number; percentage: number }> };
    B: { products: Array<{ id: string; name: string; value: number; percentage: number }> };
    C: { products: Array<{ id: string; name: string; value: number; percentage: number }> };
  };
  loading?: boolean;
}

const ProductVariantBadges: React.FC<{ product: ProductWithDetails }> = ({ product }) => {
  if (!product.colorName && !product.sizeName) return null;
  return (
    <div className="flex items-center gap-1.5 mt-0.5">
      {product.colorHex && (
        <span
          className="inline-block w-3 h-3 rounded-full border border-gray-300 dark:border-gray-500 flex-shrink-0"
          style={{ backgroundColor: product.colorHex }}
          title={product.colorName}
        />
      )}
      {product.colorName && (
        <span className="text-[10px] text-gray-500 dark:text-gray-400">{product.colorName}</span>
      )}
      {product.sizeName && (
        <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded font-medium">
          {product.sizeName}
        </span>
      )}
    </div>
  );
};

export const ProductPerformance: React.FC<ProductPerformanceProps> = ({
  topProducts,
  bottomProducts,
  abcAnalysis,
  loading = false,
}) => {
  const formatCurrency = (value: number) => {
    return `€${value.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
          <Package className="h-5 w-5 text-purple-600 dark:text-purple-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Product Performance
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Top performers, underperformers, and ABC analysis
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
            <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100">
              Top 10 Products by Revenue
            </h4>
          </div>
          {topProducts.length > 0 ? (
            <div className="space-y-3">
              {topProducts.slice(0, 10).map((product, index) => (
                <div
                  key={product.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {product.name}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {product.quantity} sold
                        </span>
                        <ProductVariantBadges product={product} />
                      </div>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {formatCurrency(product.revenue)}
                    </p>
                    <p className="text-xs text-green-600 dark:text-green-400">
                      {formatCurrency(product.profit)} profit
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              No product data available
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400" />
            <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100">
              Bottom 10 Products
            </h4>
          </div>
          {bottomProducts.length > 0 ? (
            <div className="space-y-3">
              {bottomProducts.slice(0, 10).map((product, index) => (
                <div
                  key={product.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {product.name}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {product.quantity} sold
                        </span>
                        <ProductVariantBadges product={product} />
                      </div>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {formatCurrency(product.revenue)}
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-400">
                      {formatCurrency(product.profit)} profit
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              No product data available
            </div>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-2 mb-6">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100">
            ABC Analysis (80/20 Rule)
          </h4>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Category A (80% of value)
              </h5>
              <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded">
                {abcAnalysis.A.products.length} products
              </span>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {abcAnalysis.A.products.slice(0, 10).map((product) => (
                <div
                  key={product.id}
                  className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 rounded"
                >
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
                    {product.name}
                  </span>
                  <span className="text-xs font-semibold text-green-700 dark:text-green-400 ml-2">
                    {product.percentage.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Category B (15% of value)
              </h5>
              <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-2 py-1 rounded">
                {abcAnalysis.B.products.length} products
              </span>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {abcAnalysis.B.products.slice(0, 10).map((product) => (
                <div
                  key={product.id}
                  className="flex items-center justify-between p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded"
                >
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
                    {product.name}
                  </span>
                  <span className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 ml-2">
                    {product.percentage.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Category C (5% of value)
              </h5>
              <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded">
                {abcAnalysis.C.products.length} products
              </span>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {abcAnalysis.C.products.slice(0, 10).map((product) => (
                <div
                  key={product.id}
                  className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded"
                >
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
                    {product.name}
                  </span>
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 ml-2">
                    {product.percentage.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
